import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const REASON_TEXT = {
  session_expired: 'Your session expired — please sign in again.',
  refresh_failed: 'Your session expired — please sign in again.',
};

export default function LoginPage() {
  const { login, logoutReason, user } = useAuth();
  const navigate = useNavigate();
  const reasonText = REASON_TEXT[logoutReason];
  // Already signed in (e.g. typed /login by hand) → straight back into the app.
  useEffect(() => { if (user) navigate('/', { replace: true }); }, [user, navigate]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background:
          'radial-gradient(600px at 0% 0%, rgba(59,130,246,0.10), transparent), radial-gradient(600px at 100% 100%, rgba(99,102,241,0.10), transparent), hsl(210 40% 99%)',
      }}
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="h-16 w-16 rounded-2xl bg-white border border-border/60 flex items-center justify-center mb-4 shadow-lg shadow-primary/10">
            <img src="/logo.png" alt="Futurists logo" className="h-10 w-10 object-contain" data-testid="login-logo" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Futurists Sales Tracker</h1>
          <p className="text-sm text-muted-foreground mt-1">Track pipeline, performance &amp; incentives</p>
        </div>
        <Card className="shadow-xl shadow-slate-200/60 border-border/70">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              {reasonText && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status" data-testid="login-session-notice">
                  {reasonText}
                </p>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@futurists.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="animate-spin" size={16} /> : 'Sign In'}
              </Button>
            </form>
            <p className="mt-5 text-center text-xs text-muted-foreground" data-testid="login-help-text">
              Need an account? Contact your administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
